import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppColors } from '../theme/colors';
import { useAuth } from '../hooks/useAuth';
import { updateUserProfile } from '../services/firebase/auth';
import { checkAndAwardBadges } from '../services/firebase/badges';
import { ProficiencyLevel } from '../types/firestore';
import {
  SUPPORTED_LANGUAGES,
  DAILY_GOAL_OPTIONS,
  DEFAULT_TARGET_LANGUAGE,
  DEFAULT_DAILY_GOAL,
} from '../constants/languages';
import { DEFAULT_VOICE } from '../constants/voices';
import { VoiceSelector } from '../components/voice';

interface LevelOption {
  id: ProficiencyLevel;
  name: string;
  description: string;
  emoji: string;
}

const levels: LevelOption[] = [
  {
    id: 'A1',
    name: 'A1 - Початківець',
    description: 'Тільки починаю. Вивчаю базові фрази та вирази.',
    emoji: '🌱',
  },
  {
    id: 'A2',
    name: 'A2 - Базовий',
    description: 'Можу спілкуватися в простих повсякденних ситуаціях.',
    emoji: '🌿',
  },
  {
    id: 'B1',
    name: 'B1 - Середній',
    description: 'Можу обговорювати знайомі теми та справлятися в подорожах.',
    emoji: '📚',
  },
  {
    id: 'B2',
    name: 'B2 - Вище середнього',
    description: 'Можу вільно спілкуватися та обговорювати складні теми.',
    emoji: '🎯',
  },
  {
    id: 'C1',
    name: 'C1 - Просунутий',
    description: 'Можу вільно висловлювати думки та гнучко використовувати мову.',
    emoji: '🚀',
  },
  {
    id: 'C2',
    name: 'C2 - Вільне володіння',
    description: 'Майже як носій мови. Розумію практично все.',
    emoji: '⭐',
  },
];

// Onboarding steps
type OnboardingStep = 'level' | 'language' | 'goal' | 'voice';

const LevelSelectPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Step tracking
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('level');

  // Selections
  const [selectedLevel, setSelectedLevel] = useState<ProficiencyLevel | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(DEFAULT_TARGET_LANGUAGE);
  const [selectedGoal, setSelectedGoal] = useState<number>(DEFAULT_DAILY_GOAL);
  const [selectedVoice, setSelectedVoice] = useState<string>(DEFAULT_VOICE);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleNext = () => {
    if (currentStep === 'level') {
      if (!selectedLevel) {
        setError('Будь ласка, оберіть рівень');
        return;
      }
      setError('');
      setCurrentStep('language');
    } else if (currentStep === 'language') {
      setCurrentStep('goal');
    } else if (currentStep === 'goal') {
      setCurrentStep('voice');
    }
  };

  const handleBack = () => {
    if (currentStep === 'language') {
      setCurrentStep('level');
    } else if (currentStep === 'goal') {
      setCurrentStep('language');
    } else if (currentStep === 'voice') {
      setCurrentStep('goal');
    }
  };

  const handleComplete = async () => {
    if (!selectedLevel) {
      setError('Будь ласка, оберіть рівень');
      setCurrentStep('level');
      return;
    }

    if (!user) {
      setError('Ви повинні бути авторизовані');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Save all onboarding settings in one Firestore call
      await updateUserProfile(user.uid, {
        level: selectedLevel,
        targetLanguage: selectedLanguage,
        dailyPracticeGoal: selectedGoal,
        preferredVoice: selectedVoice,
      });

      // Check and award level badges
      checkAndAwardBadges(user.uid, 'level_changed').catch((err) => {
        console.warn('[LevelSelect] Error checking badges:', err);
      });

      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Не вдалося зберегти налаштування');
    } finally {
      setLoading(false);
    }
  };

  // Progress indicator
  const steps = ['level', 'language', 'goal', 'voice'] as const;
  const currentStepIndex = steps.indexOf(currentStep);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: AppColors.bgPrimary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 'clamp(16px, 4vw, 24px)',
        paddingTop: 'clamp(24px, 6vw, 48px)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '500px',
          background: AppColors.surfaceDark,
          borderRadius: 'clamp(16px, 4vw, 24px)',
          padding: 'clamp(24px, 6vw, 40px)',
          border: `1px solid ${AppColors.borderColor}`,
        }}
      >
        {/* Progress Dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: 'clamp(20px, 5vw, 28px)',
        }}>
          {steps.map((step, index) => (
            <div
              key={step}
              style={{
                width: index <= currentStepIndex ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: index <= currentStepIndex
                  ? `linear-gradient(135deg, ${AppColors.accentPurple} 0%, ${AppColors.accentBlue} 100%)`
                  : AppColors.borderColor,
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: 'clamp(10px, 2.5vw, 14px)',
              background: 'rgba(248, 113, 113, 0.15)',
              border: `1px solid ${AppColors.errorRose}`,
              borderRadius: 'clamp(8px, 2vw, 12px)',
              marginBottom: 'clamp(16px, 4vw, 20px)',
              color: AppColors.errorRose,
              fontSize: 'clamp(13px, 3vw, 14px)',
            }}
          >
            {error}
          </div>
        )}

        {/* Step 1: Level Selection */}
        {currentStep === 'level' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 6vw, 32px)' }}>
              <div style={{ fontSize: 'clamp(40px, 10vw, 56px)', marginBottom: 'clamp(8px, 2vw, 12px)' }}>
                📊
              </div>
              <h1 style={{
                fontSize: 'clamp(22px, 5.5vw, 28px)',
                fontWeight: 700,
                color: AppColors.textPrimary,
                margin: 0,
              }}>
                Який ваш рівень англійської?
              </h1>
              <p style={{
                fontSize: 'clamp(14px, 3.5vw, 16px)',
                color: AppColors.textSecondary,
                margin: 'clamp(8px, 2vw, 12px) 0 0 0',
              }}>
                Ми підберемо уроки для вашого рівня
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 2.5vw, 12px)' }}>
              {levels.map((level) => (
                <button
                  key={level.id}
                  type="button"
                  onClick={() => setSelectedLevel(level.id)}
                  style={{
                    width: '100%',
                    padding: 'clamp(14px, 3.5vw, 18px)',
                    background: selectedLevel === level.id ? AppColors.surfaceMedium : AppColors.surfaceLight,
                    border: `2px solid ${selectedLevel === level.id ? AppColors.accentPurple : AppColors.borderColor}`,
                    borderRadius: 'clamp(12px, 3vw, 16px)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'clamp(12px, 3vw, 16px)',
                  }}
                >
                  <div style={{ fontSize: 'clamp(24px, 6vw, 32px)', flexShrink: 0 }}>
                    {level.emoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 'clamp(15px, 3.5vw, 17px)',
                      fontWeight: 600,
                      color: selectedLevel === level.id ? AppColors.textPrimary : AppColors.textSecondary,
                      marginBottom: '2px',
                    }}>
                      {level.name}
                    </div>
                    <div style={{
                      fontSize: 'clamp(12px, 2.5vw, 13px)',
                      color: AppColors.textSecondary,
                      lineHeight: 1.3,
                    }}>
                      {level.description}
                    </div>
                  </div>
                  {selectedLevel === level.id && (
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: AppColors.accentPurple,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: AppColors.textDark,
                      fontSize: '12px',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>
                      ✓
                    </div>
                  )}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={handleNext}
              disabled={!selectedLevel}
              style={{
                width: '100%',
                height: 'clamp(48px, 12vw, 56px)',
                marginTop: 'clamp(24px, 6vw, 32px)',
                background: !selectedLevel
                  ? AppColors.surfaceMedium
                  : `linear-gradient(135deg, ${AppColors.accentPurple} 0%, ${AppColors.accentBlue} 100%)`,
                border: 'none',
                borderRadius: 'clamp(10px, 2.5vw, 12px)',
                color: !selectedLevel ? AppColors.textSecondary : AppColors.textDark,
                fontSize: 'clamp(15px, 3.5vw, 17px)',
                fontWeight: 600,
                cursor: !selectedLevel ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: !selectedLevel ? 0.7 : 1,
              }}
            >
              Далі
            </button>
          </>
        )}

        {/* Step 2: Native Language Selection */}
        {currentStep === 'language' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 6vw, 32px)' }}>
              <div style={{ fontSize: 'clamp(40px, 10vw, 56px)', marginBottom: 'clamp(8px, 2vw, 12px)' }}>
                🌍
              </div>
              <h1 style={{
                fontSize: 'clamp(22px, 5.5vw, 28px)',
                fontWeight: 700,
                color: AppColors.textPrimary,
                margin: 0,
              }}>
                Яка ваша рідна мова?
              </h1>
              <p style={{
                fontSize: 'clamp(14px, 3.5vw, 16px)',
                color: AppColors.textSecondary,
                margin: 'clamp(8px, 2vw, 12px) 0 0 0',
              }}>
                Ми адаптуємо пояснення для вас
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 'clamp(10px, 2.5vw, 12px)',
            }}>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setSelectedLanguage(lang.code)}
                  style={{
                    padding: 'clamp(14px, 3.5vw, 18px)',
                    background: selectedLanguage === lang.code ? AppColors.surfaceMedium : AppColors.surfaceLight,
                    border: `2px solid ${selectedLanguage === lang.code ? AppColors.accentPurple : AppColors.borderColor}`,
                    borderRadius: 'clamp(12px, 3vw, 16px)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 'clamp(28px, 7vw, 36px)', marginBottom: '6px' }}>
                    {lang.flag}
                  </div>
                  <div style={{
                    fontSize: 'clamp(14px, 3.5vw, 15px)',
                    fontWeight: 600,
                    color: selectedLanguage === lang.code ? AppColors.textPrimary : AppColors.textSecondary,
                  }}>
                    {lang.name}
                  </div>
                </button>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: 'clamp(24px, 6vw, 32px)',
            }}>
              <button
                type="button"
                onClick={handleBack}
                style={{
                  flex: 1,
                  height: 'clamp(48px, 12vw, 56px)',
                  background: AppColors.surfaceLight,
                  border: `1px solid ${AppColors.borderColor}`,
                  borderRadius: 'clamp(10px, 2.5vw, 12px)',
                  color: AppColors.textSecondary,
                  fontSize: 'clamp(15px, 3.5vw, 17px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Назад
              </button>
              <button
                type="button"
                onClick={handleNext}
                style={{
                  flex: 2,
                  height: 'clamp(48px, 12vw, 56px)',
                  background: `linear-gradient(135deg, ${AppColors.accentPurple} 0%, ${AppColors.accentBlue} 100%)`,
                  border: 'none',
                  borderRadius: 'clamp(10px, 2.5vw, 12px)',
                  color: AppColors.textDark,
                  fontSize: 'clamp(15px, 3.5vw, 17px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Далі
              </button>
            </div>
          </>
        )}

        {/* Step 3: Daily Goal Selection */}
        {currentStep === 'goal' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 6vw, 32px)' }}>
              <div style={{ fontSize: 'clamp(40px, 10vw, 56px)', marginBottom: 'clamp(8px, 2vw, 12px)' }}>
                🎯
              </div>
              <h1 style={{
                fontSize: 'clamp(22px, 5.5vw, 28px)',
                fontWeight: 700,
                color: AppColors.textPrimary,
                margin: 0,
              }}>
                Встановіть щоденну мету
              </h1>
              <p style={{
                fontSize: 'clamp(14px, 3.5vw, 16px)',
                color: AppColors.textSecondary,
                margin: 'clamp(8px, 2vw, 12px) 0 0 0',
              }}>
                Регулярність — ключ до вивчення мови
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 2.5vw, 12px)' }}>
              {DAILY_GOAL_OPTIONS.map((option) => (
                <button
                  key={option.minutes}
                  type="button"
                  onClick={() => setSelectedGoal(option.minutes)}
                  style={{
                    width: '100%',
                    padding: 'clamp(16px, 4vw, 20px)',
                    background: selectedGoal === option.minutes ? AppColors.surfaceMedium : AppColors.surfaceLight,
                    border: `2px solid ${selectedGoal === option.minutes ? AppColors.accentPurple : AppColors.borderColor}`,
                    borderRadius: 'clamp(12px, 3vw, 16px)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: selectedGoal === option.minutes
                        ? `linear-gradient(135deg, ${AppColors.accentPurple} 0%, ${AppColors.accentBlue} 100%)`
                        : AppColors.bgSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'clamp(16px, 4vw, 18px)',
                      fontWeight: 700,
                      color: selectedGoal === option.minutes ? AppColors.textDark : AppColors.textSecondary,
                    }}>
                      {option.minutes}
                    </div>
                    <div>
                      <div style={{
                        fontSize: 'clamp(15px, 3.5vw, 17px)',
                        fontWeight: 600,
                        color: selectedGoal === option.minutes ? AppColors.textPrimary : AppColors.textSecondary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        {option.minutes} хвилин на день
                        {option.minutes === 15 && (
                          <span style={{
                            fontSize: '11px',
                            fontWeight: 500,
                            color: AppColors.successGreen,
                            background: 'rgba(34, 197, 94, 0.15)',
                            padding: '2px 8px',
                            borderRadius: '10px',
                          }}>
                            Рекомендовано
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: 'clamp(12px, 2.5vw, 13px)',
                        color: AppColors.textSecondary,
                        marginTop: '2px',
                      }}>
                        {option.description}
                      </div>
                    </div>
                  </div>
                  {selectedGoal === option.minutes && (
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: AppColors.accentPurple,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: AppColors.textDark,
                      fontSize: '12px',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>
                      ✓
                    </div>
                  )}
                </button>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: 'clamp(24px, 6vw, 32px)',
            }}>
              <button
                type="button"
                onClick={handleBack}
                style={{
                  flex: 1,
                  height: 'clamp(48px, 12vw, 56px)',
                  background: AppColors.surfaceLight,
                  border: `1px solid ${AppColors.borderColor}`,
                  borderRadius: 'clamp(10px, 2.5vw, 12px)',
                  color: AppColors.textSecondary,
                  fontSize: 'clamp(15px, 3.5vw, 17px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Назад
              </button>
              <button
                type="button"
                onClick={handleNext}
                style={{
                  flex: 2,
                  height: 'clamp(48px, 12vw, 56px)',
                  background: `linear-gradient(135deg, ${AppColors.accentPurple} 0%, ${AppColors.accentBlue} 100%)`,
                  border: 'none',
                  borderRadius: 'clamp(10px, 2.5vw, 12px)',
                  color: AppColors.textDark,
                  fontSize: 'clamp(15px, 3.5vw, 17px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Далі
              </button>
            </div>
          </>
        )}

        {/* Step 4: Voice Selection - flex layout to keep buttons above fold */}
        {currentStep === 'voice' && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 'calc(100vh - 200px)',
            maxHeight: 'calc(100vh - 120px)',
          }}>
            {/* Header - fixed height */}
            <div style={{ textAlign: 'center', marginBottom: 'clamp(16px, 4vw, 24px)', flexShrink: 0 }}>
              <div style={{ fontSize: 'clamp(36px, 8vw, 48px)', marginBottom: 'clamp(6px, 1.5vw, 10px)' }}>
                🎤
              </div>
              <h1 style={{
                fontSize: 'clamp(20px, 5vw, 26px)',
                fontWeight: 700,
                color: AppColors.textPrimary,
                margin: 0,
              }}>
                Оберіть голос репетитора
              </h1>
              <p style={{
                fontSize: 'clamp(13px, 3vw, 15px)',
                color: AppColors.textSecondary,
                margin: 'clamp(6px, 1.5vw, 10px) 0 0 0',
              }}>
                Знайдіть голос, з яким вам буде приємно вчитися
              </p>
            </div>

            {/* Voice list - scrollable, takes remaining space */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              marginBottom: 'clamp(12px, 3vw, 16px)',
              minHeight: 0, // Important for flex scroll
            }}>
              <VoiceSelector
                selectedVoice={selectedVoice}
                onVoiceSelect={setSelectedVoice}
                showHint
              />
            </div>

            {/* Buttons - fixed at bottom */}
            <div style={{
              display: 'flex',
              gap: '12px',
              flexShrink: 0,
              paddingTop: '8px',
            }}>
              <button
                type="button"
                onClick={handleBack}
                style={{
                  flex: 1,
                  height: 'clamp(48px, 12vw, 56px)',
                  background: AppColors.surfaceLight,
                  border: `1px solid ${AppColors.borderColor}`,
                  borderRadius: 'clamp(10px, 2.5vw, 12px)',
                  color: AppColors.textSecondary,
                  fontSize: 'clamp(15px, 3.5vw, 17px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Назад
              </button>
              <button
                type="button"
                onClick={handleComplete}
                disabled={loading}
                style={{
                  flex: 2,
                  height: 'clamp(48px, 12vw, 56px)',
                  background: loading
                    ? AppColors.surfaceMedium
                    : `linear-gradient(135deg, ${AppColors.accentPurple} 0%, ${AppColors.accentBlue} 100%)`,
                  border: 'none',
                  borderRadius: 'clamp(10px, 2.5vw, 12px)',
                  color: loading ? AppColors.textSecondary : AppColors.textDark,
                  fontSize: 'clamp(15px, 3.5vw, 17px)',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Збереження...' : 'Почати навчання'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LevelSelectPage;
